import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, X, ArrowRight, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import api from "../utils/api";

// Nudges new users to complete Guided Setup.
//  • One-time welcome modal on first login (recent signups) — "Start setup" / "Skip".
//  • A slim, dismissible banner that persists until onboarding is complete.
// Both make clear that some features won't work correctly until setup is done.
const OnboardingNudge = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(true);

  const uid = user?.id || "anon";
  const welcomeKey = `qs_onboarding_welcome_${uid}`;
  const bannerKey = `qs_onboarding_banner_dismissed_${uid}`;

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get("/onboarding/status")
      .then((res) => {
        if (cancelled) return;
        setStatus(res.data);
        const complete = res.data?.is_complete;
        if (complete) return; // fully set up — never nudge

        setBannerDismissed(localStorage.getItem(bannerKey) === "1");

        // Auto-open the welcome modal once for recent signups.
        const seenWelcome = localStorage.getItem(welcomeKey) === "1";
        const createdAt = user?.created_at ? new Date(user.created_at) : null;
        const isRecent = createdAt
          ? (Date.now() - createdAt.getTime()) < 30 * 24 * 60 * 60 * 1000
          : true; // unknown age → treat as new (conservative nudge)
        if (!seenWelcome && isRecent) setShowModal(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user, uid]);

  const closeModal = () => {
    localStorage.setItem(welcomeKey, "1");
    setShowModal(false);
  };

  const startSetup = () => {
    localStorage.setItem(welcomeKey, "1");
    setShowModal(false);
    navigate("/onboarding");
  };

  const dismissBanner = () => {
    localStorage.setItem(bannerKey, "1");
    setBannerDismissed(true);
  };

  // Don't nag while the user is already inside the onboarding wizard.
  const onOnboardingPage = location.pathname.startsWith("/onboarding");
  const incomplete = status && !status.is_complete;

  return (
    <>
      {/* Persistent banner */}
      {incomplete && !bannerDismissed && !onOnboardingPage && (
        <div
          className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2.5"
          data-testid="onboarding-nudge-banner"
        >
          <div className="max-w-6xl mx-auto flex items-center gap-3">
            <Sparkles className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm flex-1 min-w-0">
              <span className="font-semibold">Finish setting up QuoteSorted</span>
              <span className="hidden sm:inline text-white/80"> — some features won't work correctly until your setup is complete.</span>
            </p>
            <Button
              size="sm"
              onClick={() => navigate("/onboarding")}
              className="bg-white text-blue-700 hover:bg-blue-50 h-7 px-3 text-xs font-semibold flex-shrink-0"
              data-testid="onboarding-nudge-banner-start"
            >
              Start setup
              <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
            <button
              onClick={dismissBanner}
              className="text-white/70 hover:text-white flex-shrink-0"
              aria-label="Dismiss"
              data-testid="onboarding-nudge-banner-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* First-login welcome modal */}
      {showModal && incomplete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" data-testid="onboarding-nudge-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-6 text-center">
              <div className="w-16 h-16 mx-auto bg-white/15 rounded-2xl flex items-center justify-center mb-3">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">Welcome to QuoteSorted!</h2>
              <p className="text-white/85 text-sm mt-1">Let's get your account set up in a few guided steps.</p>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Our guided setup walks you through your branding, documents, payments and the tools you'll use every day — with tips at every step. It only takes a few minutes.
              </p>
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  Some features won't work correctly until you've completed this setup — so it's worth doing now.
                </p>
              </div>
              <div className="flex flex-col gap-2 pt-1">
                <Button
                  onClick={startSetup}
                  className="w-full bg-blue-600 hover:bg-blue-700 gap-2"
                  data-testid="onboarding-nudge-modal-start"
                >
                  <Sparkles className="w-4 h-4" />
                  Start guided setup
                </Button>
                <Button
                  variant="ghost"
                  onClick={closeModal}
                  className="w-full text-gray-500"
                  data-testid="onboarding-nudge-modal-skip"
                >
                  Skip for now
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OnboardingNudge;
